import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/utils/cn';

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  leadingIcon?: ReactNode;
}

/**
 * A labelled text input with accessible error reporting.
 *
 * <p>The accessibility wiring here is the reason this is a component rather than a bare
 * `<input>`: `aria-describedby` links the error to the field, `aria-invalid` marks it,
 * and `role="alert"` announces it. Screen-reader users otherwise hear the label and
 * nothing about why the form will not submit.
 */
export function Field({ label, error, hint, leadingIcon, className, ...rest }: FieldProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ');

  return (
    <div className="w-full">
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}
      </label>
      <div className="relative">
        {leadingIcon && (
          <span
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          >
            {leadingIcon}
          </span>
        )}
        <input
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          className={cn(
            'h-11 w-full rounded-xl bg-white text-slate-900 ring-1 transition-shadow',
            'placeholder:text-slate-400',
            leadingIcon ? 'pl-10 pr-3' : 'px-3',
            error
              ? 'ring-red-400 focus:ring-2 focus:ring-red-500'
              : 'ring-slate-300 focus:ring-2 focus:ring-brand-600',
            className,
          )}
          {...rest}
        />
      </div>
      {hint && !error && (
        <p id={hintId} className="mt-1 text-xs text-slate-500">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-xs font-medium text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
